import { lowerCase } from 'lodash';
import {
  Grid,
  GridCellProps,
  GridColumn as Column,
  GridDataStateChangeEvent,
  GridNoRecords
} from '@progress/kendo-react-grid';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader } from '@progress/kendo-react-indicators';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { State, toDataSourceRequestString } from '@progress/kendo-data-query';

import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../components/grid/grid-tool-bar';
import PageToolbar from '../components/page-toolbar';
import { formatDateToDDMMYYYY } from '../utils/date';
import HelpSupport from '../components/help-support';
import { ColumnMenu } from '../components/columnMenu';
import { NoRecords } from '../components/grid/no-records';
import { DefaultGridSettings } from '../constants/grid-settings';
import { useGetEventReports } from '../hooks/services/use-reports';
import { EventReportResponse } from '../types/responses/event-report-response';

const initialDataState: State = {
  sort: [{ field: 'actualStartDateUtc', dir: 'desc' }],
  ...DefaultGridSettings.initialDataState
};

const ReportsGrid = () => {
  const navigate = useNavigate();
  const [dataState, setDataState] = useState<State>(initialDataState);
  const [gridMode, setGridMode] = useState<string>('grid');

  const { data, isLoading } = useGetEventReports(
    toDataSourceRequestString(dataState)
  );

  const onColumnTitleClick = (props: GridCellProps) => {
    const { id, detailId, type } = props.dataItem as EventReportResponse;
    return (
      <td className={'k-table-td'}>
        <span
          className={'link-primary cursor-pointer p-0 text-start'}
          onClick={() =>
            navigate(`../reports/${id}/${detailId}/${lowerCase(type)}`)
          }>
          {props.dataItem.templateName}
        </span>
      </td>
    );
  };

  const gridExportRef = useRef<ExcelExport | null>(null);

  const DateCell = (props: GridCellProps) => {
    return (
      <td className={'k-table-td'}>
        {formatDateToDDMMYYYY(props.dataItem.actualStartDateUtc, true)}
      </td>
    );
  };

  return (
    <>
      <PageToolbar title={'My reports'}>
        <HelpSupport
          title="Reports guide"
          url="https://forum.catalystglobal.com/t/4288"
        />
      </PageToolbar>

      <div
        className={'pt-2'}
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          width: '100%',
          overflow: 'hidden'
        }}>
        <GridToolBar
          showCardMode={false}
          searchPlaceholder={'Search reports'}
          columnsToSearch={['name']}
          exportRef={gridExportRef}
          onGridModeChange={(e) => {
            setGridMode(e.gridMode);
          }}
          {...dataState}
          onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}></GridToolBar>
        <ExcelExport
          data={data?.data ?? []}
          ref={gridExportRef}
          fileName={`reports.xlsx`}>
          {gridMode === 'grid' && (
            <Grid
              id={'grid-reports'}
              style={{
                flexGrow: 1,
                minHeight: 0,
                height: '100%',
                overflow: 'hidden'
              }}
              pageable={DefaultGridSettings.pagerSettings}
              sortable={true}
              data={data}
              {...dataState}
              onDataStateChange={(e: GridDataStateChangeEvent) => {
                setDataState(e.dataState);
              }}>
              <Column
                field={'actualStartDateUtc'}
                title={'Date'}
                cells={{ data: DateCell }}
              />
              <Column
                field={'name'}
                title={'Event Title'}
                columnMenu={ColumnMenu}
              />
              <Column
                field={'templateName'}
                title={'Activity Title'}
                columnMenu={ColumnMenu}
                cells={{ data: onColumnTitleClick }}
              />
              <Column field={'type'} title={'Type'} columnMenu={ColumnMenu} />
              <Column
                field={'agencyOrClient'}
                title={'Client'}
                columnMenu={ColumnMenu}
              />

              <GridNoRecords>
                {isLoading ? (
                  <div
                    className={
                      'd-flex flex-column align-items-center gap-2 text-muted'
                    }>
                    <Loader type={'pulsing'} />
                    <span>Loading data</span>
                  </div>
                ) : (
                  <NoRecords />
                )}
              </GridNoRecords>
            </Grid>
          )}
        </ExcelExport>
      </div>
    </>
  );
};

export default ReportsGrid;
